import { PaletteOptions } from '@mui/material/styles'

// colors  https://www.figma.com/design/k2QwrSWepucdwCnwbMFsm1/Flowcode-Website--Internal-exploratory-3-24-?node-id=5871-27623&p=f&m=dev
const filledHoverShade = 'rgba(255, 255, 255, 0.16)'
const filledFocusShade = 'rgba(255, 255, 255, 0.24)'
const hexToRGB = (hex: string): number[] => {
  const hexString = hex.replace('#', '')
  const r = parseInt(hexString.substring(0, 2), 16)
  const g = parseInt(hexString.substring(2, 4), 16)
  const b = parseInt(hexString.substring(4, 6), 16)
  return [r, g, b]
}

const withAlphaRGB = (rgb: number[], alpha: number): string => `rgba(${rgb.join(', ')}, ${alpha})`
const baseRGBTheme3 = {
  primary: [35, 35, 35],
  secondary: [35, 35, 35],
  tertiary: [26, 86, 219],
  error: [186, 26, 26],
  shades: {
    primary: [35, 35, 35],
    primaryFilled: [255, 255, 255],
    tertiary: [26, 86, 219]
  }
}
const baseFlow3Colors = {
  primary: {
    light: '#393939',
    main: '#232323',
    dark: '#111111',
    contrastText: '#FFFFFF'
  },
  secondary: {
    light: withAlphaRGB(baseRGBTheme3.secondary, 0.08),
    main: withAlphaRGB(baseRGBTheme3.secondary, 0.16),
    dark: withAlphaRGB(baseRGBTheme3.secondary, 0.32),
    contrastText: '#232323'
  },
  tertiary: {
    light: '#36BCFF',
    main: '#1A56DB',
    dark: '#1546B8',
    contrastText: '#FFFFFF'
  },
  error: {
    light: '#DE3730',
    main: '#BA1A1A',
    dark: '#93000A',
    contrastText: '#FFFFFF'
  },
  info: {
    light: '#36BCFF',
    main: '#1A56DB',
    dark: '#1546B8',
    contrastText: '#FFFFFF'
  },
  warning: {
    light: '#F19547',
    main: '#D27B30',
    dark: '#B26317',
    contrastText: '#232323'
  },
  upgrade: {
    main: '#F8BB68',
    light: 'rgb(249, 200, 134)',
    dark: 'rgb(173, 130, 72)',
    contrastText: 'rgba(0, 0, 0, 0.87)'
  },
  success: {
    light: '#33D99D',
    main: '#00C885',
    dark: '#008F5C',
    contrastText: '#FFFFFF'
  }
}

export const flow2Palette: PaletteOptions = {
  mode: 'light',
  ...baseFlow3Colors,
  text: {
    primary: '#232323',
    secondary: '#5A5A5A',
    disabled: withAlphaRGB(baseRGBTheme3.primary, 0.38)
  },
  divider: withAlphaRGB(baseRGBTheme3.secondary, 0.24),
  background: {
    paper: '#FFFFFF',
    default: '#FFFFFF'
  },
  action: {
    active: withAlphaRGB(baseRGBTheme3.primary, 0.54),
    hover: withAlphaRGB(baseRGBTheme3.primary, 0.08),
    selected: withAlphaRGB(baseRGBTheme3.primary, 0.12),
    disabled: withAlphaRGB(baseRGBTheme3.primary, 0.12),
    disabledBackground: withAlphaRGB(baseRGBTheme3.primary, 0.38),
    focus: withAlphaRGB(baseRGBTheme3.primary, 0.16),
    tooltip: 'rgba(31, 31, 31, 0.9)'
  },
  shades: {
    primary: {
      hover: withAlphaRGB(baseRGBTheme3.secondary, 0.08),
      focus: withAlphaRGB(baseRGBTheme3.secondary, 0.16),
      selected: withAlphaRGB(baseRGBTheme3.secondary, 0.16),
      active: withAlphaRGB(baseRGBTheme3.secondary, 0.32),
      filledHover: `linear-gradient(0deg, ${withAlphaRGB(
        baseRGBTheme3.shades.primaryFilled,
        0.16
      )}, ${withAlphaRGB(baseRGBTheme3.shades.primaryFilled, 0.16)}), ${withAlphaRGB(baseRGBTheme3.primary, 1)}`,
      filledFocus: `linear-gradient(0deg, ${withAlphaRGB(
        baseRGBTheme3.shades.primaryFilled,
        0.24
      )}, ${withAlphaRGB(baseRGBTheme3.shades.primaryFilled, 0.24)}), ${withAlphaRGB(baseRGBTheme3.primary, 1)}`
    },
    secondary: {
      filledHover: `linear-gradient(0deg, ${withAlphaRGB(
        baseRGBTheme3.primary,
        0.08
      )}, ${withAlphaRGB(baseRGBTheme3.primary, 0.08)}), ${withAlphaRGB(baseRGBTheme3.secondary, 0.16)}`,
      filledFocus: `linear-gradient(0deg, ${withAlphaRGB(
        baseRGBTheme3.primary,
        0.16
      )}, ${withAlphaRGB(baseRGBTheme3.primary, 0.16)}), ${withAlphaRGB(baseRGBTheme3.secondary, 0.16)}`
    },
    tertiary: {
      hover: withAlphaRGB(baseRGBTheme3.shades.tertiary, 0.08),
      focus: withAlphaRGB(baseRGBTheme3.shades.tertiary, 0.16),
      filledHover: `linear-gradient(0deg, ${filledHoverShade}, ${filledHoverShade}), ${withAlphaRGB(
        baseRGBTheme3.tertiary,
        1
      )}`,
      filledFocus: `linear-gradient(0deg, ${filledFocusShade}, ${filledFocusShade}), ${withAlphaRGB(
        baseRGBTheme3.tertiary,
        1
      )}`,
      filledDisabled: `linear-gradient(0deg, ${withAlphaRGB(
        baseRGBTheme3.tertiary,
        0.38
      )}, ${withAlphaRGB(baseRGBTheme3.tertiary, 0.38)}), #FFFFFF`
    },
    error: {
      hover: withAlphaRGB(baseRGBTheme3.error, 0.08),
      focus: withAlphaRGB(baseRGBTheme3.error, 0.16),
      filledHover: `linear-gradient(0deg, ${filledHoverShade}, ${filledHoverShade}), ${withAlphaRGB(
        baseRGBTheme3.error,
        1
      )}`,
      filledFocus: `linear-gradient(0deg, ${filledFocusShade}, ${filledFocusShade}), ${withAlphaRGB(
        baseRGBTheme3.error,
        1
      )}`
    },
    upgrade: {
      hover: baseFlow3Colors.warning.main,
      filledFocus: `linear-gradient(0deg, ${filledFocusShade}, ${filledFocusShade}), ${withAlphaRGB(
        hexToRGB(baseFlow3Colors.upgrade.main),
        1
      )}`
    }
  },
  surface: {
    0: '#FFFFFF',
    1: '#F5F5F5',
    2: '#EEEEEE',
    3: '#E8E8E8',
    4: '#E3E3E3',
    5: '#D6D6D6',
    inverse: '#232323',
    disabled: withAlphaRGB(baseRGBTheme3.primary, 0.12),
    tintLight: '#7A7A7A',
    tintDark: '#E3E3E3'
  },
  onSurface: {
    primary: '#232323',
    secondary: '#5A5A5A',
    disabled: withAlphaRGB(baseRGBTheme3.primary, 0.38),
    inverse: '#FFFFFF',
    inverseSecondary: '#C6C6C6',
    inverseTertiary: '#7BDA9D'
  },
  border: {
    outline: '#C6C6C6',
    divider: withAlphaRGB(baseRGBTheme3.secondary, 0.24),
    disabled: withAlphaRGB(baseRGBTheme3.primary, 0.12)
  },
  utility: {
    scrim: withAlphaRGB(baseRGBTheme3.primary, 0.32),
    tooltip: withAlphaRGB(baseRGBTheme3.primary, 0.9),
    upgrade: baseFlow3Colors.upgrade.main
  },
  container: {
    error: '#FFDAD6',
    warning: '#FFF1CC',
    info: '#D8E2FF',
    success: '#CEF3D8'
  }
}
